import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Virtuoso } from 'react-virtuoso';
import { setMeetingToEdit, setMeetingModalOpen } from '../../../stores/actions/session';
import { setListSortType, setListShowPast, setListShowAll } from '../../../stores/actions/user-interface';
import MeetingRow from '../meeting-row';
import { APP } from '../../../stores/session-status';
import type { Meeting } from '../../../stores/types/session';
import { GreenroomState } from '../../../stores/types/types';
import { setHomeMeeting } from '../../../stores/actions/home';
import SlidingMenu, { SlidingMenuOption } from '../../general-components/sliding-menu';

const tabOptions = [
	{ label: 'Upcoming', data: false },
	{ label: 'Past', data: true },
];

interface Props {
	searchString: string;
	filteredEvents: any;
	connected: boolean;
	join: (event: any) => void;
	forPresenter: boolean;
	creatingMeeting?: any;
	awaitingProducer?: boolean;
}

export default function EventList(props: Props) {
	const liveChannels = useSelector((state: GreenroomState) => state.ContentReducer.liveChannels);
	const currentRoute = useSelector((state: GreenroomState) => state.HomeReducer.currentRoute);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const appSignedInWith = useSelector(
		(state: GreenroomState) => state.SessionReducer.appSignedInWith,
	);
	const meetingModalOpen = useSelector(
		(state: GreenroomState) => state.SessionReducer.meetingModalOpen,
	);
	const listSortType = useSelector(
		(state: GreenroomState) => state.UserInterfaceReducer.listSortType,
	);
	const listShowPast = useSelector(
		(state: GreenroomState) => state.UserInterfaceReducer.listShowPast,
	);
	const listShowAll = useSelector(
		(state: GreenroomState) => state.UserInterfaceReducer.listShowAll,
	);
	const mainVideoStream = useSelector((state: GreenroomState) => state.VideoReducer.mainVideoStream);
	const restrictedUserState = useSelector((state: GreenroomState) => state.SessionReducer.restrictedUserState);

	const dispatch = useDispatch();
	const {
		searchString,
		filteredEvents,
		connected,
		join,
		forPresenter,
		creatingMeeting = null,
		awaitingProducer = false,
	} = props;

	const [displayedEventCount, setDisplayedEventCount] = useState(0);

	function sortTime() {
		if (listSortType === 'none') {
			dispatch(setListSortType('time-asc'));
		} else if (listSortType === 'time-asc') {
			dispatch(setListSortType('time-desc'));
		} else {
			dispatch(setListSortType('none'));
		}
	}

	function sortShow() {
		if (listSortType === 'none') {
			dispatch(setListSortType('show-asc'));
		} else if (listSortType === 'show-asc') {
			dispatch(setListSortType('show-desc'));
		} else {
			dispatch(setListSortType('none'));
		}
	}

	function pastEventFilter() {
		if (listShowPast) {
			return filteredEvents.filter((event: any) => new Date(event.end_time) < new Date());
		}
		return filteredEvents.filter((event: any) => new Date(event.end_time) >= new Date());
	}

	function editMeeting(meetingID: number) {
		const filteredMeeting = filteredEvents.find(
			(meeting: Meeting) => meeting.meeting === meetingID,
		);
		dispatch(setMeetingToEdit(filteredMeeting));
		dispatch(setMeetingModalOpen(true));
	}

	useEffect(() => {
		if (!meetingModalOpen && currentRoute === 1) {
			// Added meeting for editing, removing again when on home page
			dispatch(setHomeMeeting(null));
		}
	}, [meetingModalOpen, currentRoute]);

	const displayEvents = useMemo((): JSX.Element[] => {
		const displayedEvents = pastEventFilter();
		const selectedEvents = listShowAll ? displayedEvents : displayedEvents.slice(0, 5);

		setDisplayedEventCount(displayedEvents.length);

		return selectedEvents.map((event: any, index: number) => (
				<React.Fragment key={index}>
					{/* This is for highlighting/displaying onboarding bubble */}
					{(forPresenter && user) ? (
							<MeetingRow
								key={index}
								{...event}
								onClick={join}
								connected={connected}
								forPresenter={forPresenter}
								isLive={liveChannels.includes(event.meeting) || event.entry_without_producer}
								standaloneGreenroomProducer={appSignedInWith === APP.GREENROOM}
								onEditClicked={editMeeting}
								canEdit={true}
							/>
					) : (
						<MeetingRow
							key={index}
							{...event}
							onClick={join}
							connected={connected}
							buttonDisabled={event === creatingMeeting}
							standaloneGreenroomProducer={appSignedInWith === APP.GREENROOM}
							onEditClicked={editMeeting}
							canEdit={true}
						/>
					)}
				</React.Fragment>
		));
	}, [
		filteredEvents,
		connected,
		listShowAll,
		listShowPast,
		creatingMeeting,
		liveChannels,
		awaitingProducer,
		mainVideoStream,
		restrictedUserState,
	]);

	return (
		<>
			<SlidingMenu
				options={tabOptions}
				selectedOption={
					tabOptions.find((op: SlidingMenuOption) => op.data === listShowPast) ?? tabOptions[0]
				}
				callback={(option: SlidingMenuOption) => { dispatch(setListShowPast(option.data)); }}
				className='upcoming-past-container position-relative width-calc-full'
				fullWidthDivider={true}
			/>
			<div className="events-wrapper">
				<div className="events-header">
					<div className={classNames('sort time', { active: listSortType.includes('time') })} onClick={sortTime}>
						<label>Time</label>
						<div className={classNames('sort-type-icon', { asc: listSortType === 'time-asc', desc: listSortType === 'time-desc' })}>
							<span className="up">{'\u25B2'}</span>
							<span className="down">{'\u25BC'}</span>
						</div>
					</div>
					<div className={classNames('sort show', { active: listSortType.includes('show') })} onClick={sortShow}>
						<label>Show</label>
						<div className={classNames('sort-type-icon', { asc: listSortType === 'show-asc', desc: listSortType === 'show-desc' })}>
							<span className="up">{'\u25B2'}</span>
							<span className="down">{'\u25BC'}</span>
						</div>
					</div>
					<div className="presenters">
						<label>Presenters</label>
					</div>
				</div>
				<div className={classNames('virtualized-events-list', { expanded: listShowAll })}>
					<Virtuoso
						style={{ minHeight: '10px' }}
						useWindowScroll
						overscan={20}
						totalCount={
							displayEvents.length
						}
						customScrollParent={window.document.getElementById('home-body-scroller') ?? window.document.body}
						itemContent={(index) => (
							<div style={{ paddingBottom: '8px' }}>{displayEvents?.[index]}</div> ?? <></>
						)}
					/>
				</div>
				{displayedEventCount > 0 && (
					<div className="show-more" onClick={() => dispatch(setListShowAll(!listShowAll))}>
						{displayedEventCount > 5
							? <button>{listShowAll ? 'Show less' : 'Show more'}</button>
							: null
						}
					</div>
				)}
				{displayedEventCount === 0 && (
					<div className="no-results">
						{searchString
							? <p>No {listShowPast ? 'past' : 'upcoming'} results for <strong>"{searchString}"</strong></p>
							: <p>{listShowPast ? 'There are no previous broadcasts' : 'There are no upcoming broadcasts scheduled'}</p>
						}
					</div>
				)}
			</div>
		</>
	);
}
